'use client';

import React, { useEffect, useState } from 'react';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { cn } from '@/lib/utils';

interface LogoProps {
  className?: string;
  collapsed?: boolean;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// Base path for the admin dashboard - empty for dedicated domain
const BASE_PATH = '';

export function LanguageAwareLogo({
  className,
  collapsed = false,
  showText = true,
  size = 'md'
}: LogoProps) {
  const { language, isRTL, t: tCommon } = useCommonTranslation();
  const [imageError, setImageError] = useState(false);

  // CRITICAL: Store language and isRTL in state to prevent hydration mismatch
  const [stableLanguage, setStableLanguage] = useState('en');
  const [stableIsRTL, setStableIsRTL] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Handle client-side mounting
  useEffect(() => {
    setIsMounted(true);
    setStableLanguage(language);
    setStableIsRTL(isRTL);
  }, [language, isRTL]);

  // Preload logos to prevent flicker during transitions
  useEffect(() => {
    const preloadLogos = () => {
      const logos = [`${BASE_PATH}/images/logo-ar.svg`, `${BASE_PATH}/images/logo-en.svg`];
      logos.forEach(logo => {
        const img = new Image();
        img.src = logo;
      });
    };

    preloadLogos();
  }, []);

  // Logo dimensions based on size
  const dimensions = {
    sm: { width: 120, height: 58 },  // Smaller for mobile/collapsed
    md: { width: 140, height: 68 },  // Standard sidebar size
    lg: { width: 160, height: 78 }   // Larger for header/mobile
  };

  const { width, height } = dimensions[size];

  // Determine which logo to use - use stable language after hydration
  // Include BASE_PATH to ensure logos are loaded from the correct location
  const logoSrc = isMounted && stableLanguage === 'ar'
    ? `${BASE_PATH}/images/logo-ar.svg`
    : `${BASE_PATH}/images/logo-en.svg`;

  // Determine alt text - use stable language after hydration
  const logoAlt = isMounted && stableLanguage === 'ar'
    ? 'شعار حويس'
    : 'JOYOUS Logo';

  // Removed loading state - logos are preloaded to prevent flicker

  if (imageError) {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        <div className={cn(
          "bg-gradient-to-br from-pink-500 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300",
          size === 'sm' ? 'w-6 h-6' : size === 'md' ? 'w-8 h-8' : 'w-10 h-10'
        )}>
          <span className={cn(
            "text-white font-bold drop-shadow-sm",
            size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'
          )}>
            {isMounted && stableLanguage === 'ar' ? 'ج' : 'J'}
          </span>
        </div>
        </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 transition-all duration-300",
        collapsed && "justify-center",
        className
      )}
      suppressHydrationWarning
    >
      <img
        src={logoSrc}
        alt={logoAlt}
        width={width}
        height={height}
        className={cn(
          "object-contain transition-all duration-300 filter dark:brightness-0 dark:invert",
          collapsed && "mx-auto",
          "hover:opacity-80"
        )}
        onError={() => {
          console.error(`Failed to load logo: ${logoSrc}`);
          setImageError(true);
        }}
        onLoad={() => {
          // Clear any previous error state when image loads successfully
          if (imageError) setImageError(false);
        }}
        loading="eager"
        fetchPriority="high"
        style={{
          maxWidth: '100%',
          height: 'auto'
        }}
      />

      </div>
  );
}